import axios from 'axios'
import { useEffect } from 'react'
import {
	NavigateFunction,
	useNavigate,
	useSearchParams,
} from 'react-router-dom'
import AppUrls from '../constants/pageUrl'
import authRequests from '../requests/auth/authRequest'
import { queryClient } from '../requests/queryClient'
import useSystemStore from '../systemStore/systemStore'
import { SystemStoreType } from '../systemStore/systemStoreTypes'

// Имя ключа в LocalStorage где временно будет храниться сгенерированный токен для получения токена авторизации и данных пользователя.
export enum AutoLoginLSKeys {
	Token = 'tokenForAutoLogin',
}

export enum AutoLoginQueryKeys {
	Token = 'token',
	LegacyToken = 'tokenForAutoLogin',
}

export type AutoLoginErrorCode = 'missing-token' | 'invalid-token' | 'unknown'

export type AutoLoginError = {
	code: AutoLoginErrorCode
	message?: string
}

export type AutoLoginHandlers = {
	onSuccess?: () => void
	onError?: (error: AutoLoginError) => void
}

/**
 * Класс с логикой для автоматического входа на сайт школы если вошли на корневой сайн.
 * При входе в учётную запись на корневом сайте в LocalStorage будут записаны введённую почту и пароль для входа.
 * Другой код на корневом сайте, делающий переадресацию на сайт школы, ищет в LocalStorage записанную почту и пароль и подставляет в адресную строку при переадресации
 * Сайт школы прочитает это из адресной строки и сложит в свой LocalStorage.
 * При заходе на страницу логина школы эти данные будут прочитаны из LocalStorage и будет сделан автоматический вход уже на сайте школы.
 */
class AutoLogin {
	/**
	 * Ставит в LocalStorage сгенерированный токен для получения по нему токена авторизации, чтобы использовать для автовхода в учётную запись на сайте школы.
	 * @param generatedToken — сгенерированный токен для получения по нему токена авторизации
	 */
	setGeneratedTokenToLocalStorage(generatedToken: string) {
		localStorage.setItem(AutoLoginLSKeys.Token, generatedToken)
	}

	/** Получает почту и пароль из LocalStorage */
	getCredentialsFromLocalStorage() {
		return {
			[AutoLoginLSKeys.Token]: localStorage.getItem(
				AutoLoginLSKeys.Token
			),
		}
	}
	/** Убирает сгенерированный токен из LocalStorage */
	clearCredentialsInLocalStorage() {
		localStorage.removeItem(AutoLoginLSKeys.Token)
	}

	useMoveGeneratedTokenFromUrlToLocalStorage() {
		let [searchParams] = useSearchParams()

		useEffect(() => {
			const generatedToken =
				searchParams.get(AutoLoginQueryKeys.Token) ||
				searchParams.get(AutoLoginQueryKeys.LegacyToken)
			if (!generatedToken) return

			this.setGeneratedTokenToLocalStorage(generatedToken)
		}, [])
	}

	getUrlWithCredentials(url: string) {
		const { tokenForAutoLogin } = this.getCredentialsFromLocalStorage()

		if (!tokenForAutoLogin) {
			return url
		}

		return (
			url + '?' + this.createQueryUrlWithGeneratedToken(tokenForAutoLogin)
		)
	}

	createQueryUrlWithGeneratedToken(generatedToken: string) {
		return AutoLoginQueryKeys.Token + '=' + generatedToken
	}
	/**
	 * При загрузке страницы запускает функцию ищущую в localStorage почту и пароль для автоматического входа
	 * И если находит, делает запрос на вход.
	 */
	useMakeAutoLogin() {
		const navigate = useNavigate()
		const updateSystemStore = useSystemStore((store) => store.updateStore)

		useEffect(() => {
			this.makeAutoLogin(navigate, updateSystemStore)
		}, [])
	}

	useMakeAutoLoginWithRedirect(redirectUrl?: string) {
		const navigate = useNavigate()
		const updateSystemStore = useSystemStore((store) => store.updateStore)

		useEffect(() => {
			this.makeAutoLogin(navigate, updateSystemStore, redirectUrl)
		}, [])
	}

	useMakeAutoLoginWithRedirectAndHandlers(
		redirectUrl?: string,
		handlers?: AutoLoginHandlers,
		isEnabled = true
	) {
		const navigate = useNavigate()
		const updateSystemStore = useSystemStore((store) => store.updateStore)

		useEffect(() => {
			if (!isEnabled) return

			this.makeAutoLogin(
				navigate,
				updateSystemStore,
				redirectUrl,
				handlers
			)
		}, [handlers, isEnabled, navigate, redirectUrl, updateSystemStore])
	}

	useMakeAutoLoginWithRedirectAndHandlersAndToken(
		tokenOverride?: string,
		redirectUrl?: string,
		handlers?: AutoLoginHandlers,
		isEnabled = true
	) {
		const navigate = useNavigate()
		const updateSystemStore = useSystemStore((store) => store.updateStore)

		useEffect(() => {
			if (!isEnabled) return

			this.makeAutoLogin(
				navigate,
				updateSystemStore,
				redirectUrl,
				handlers,
				tokenOverride
			)
		}, [
			handlers,
			isEnabled,
			navigate,
			redirectUrl,
			tokenOverride,
			updateSystemStore,
		])
	}

	/**
	 * Функция ищет в localStorage сгенерированный токен для автоматического входа.
	 * Если находит, делает запрос на получение данных пользователя по этому токену.
	 * После данные стираются из localStorage.
	 */
	async makeAutoLogin(
		navigate: NavigateFunction,
		updateSystemStore: SystemStoreType.UpdateStore,
		redirectUrl?: string,
		handlers?: AutoLoginHandlers,
		tokenOverride?: string
	) {
		const overrideToken =
			typeof tokenOverride === 'string' ? tokenOverride.trim() : undefined
		const genToken =
			overrideToken !== undefined
				? overrideToken
				: localStorage.getItem(AutoLoginLSKeys.Token)

		if (overrideToken) {
			this.setGeneratedTokenToLocalStorage(overrideToken)
		}

		if (!genToken) {
			handlers?.onError?.({
				code: 'missing-token',
				message: 'В ссылке нет токена авторизации.',
			})
			return
		}

		try {
			// Отправить запрос на авторизацию по токену
			const loginResponse = await authRequests.magicLinkConsume({
				token: genToken,
			})
			if (loginResponse.status !== 200) {
				handlers?.onError?.({
					code: 'invalid-token',
					message: 'Ссылка недействительна или устарела.',
				})
				return
			}

			// Clear queries cached under the previous session before switching user.
			queryClient.clear()
			localStorage.setItem('token', loginResponse.data.accessToken)

			setTimeout(() => {
				updateSystemStore({ user: loginResponse.data.user })
				navigate(redirectUrl || AppUrls.Main().url)
				handlers?.onSuccess?.()
			}, 30)

			this.clearCredentialsInLocalStorage()
		} catch (err) {
			let errorMessage = 'Ссылка недействительна или устарела.'

			if (axios.isAxiosError(err)) {
				const responseData = err.response?.data as {
					fields?: { token?: string }
				}
				const tokenError = responseData?.fields?.token
				if (typeof tokenError === 'string' && tokenError.trim()) {
					errorMessage = tokenError
				}
			}

			handlers?.onError?.({
				code: 'invalid-token',
				message: errorMessage,
			})
		}
	}
}

const autoLogin = new AutoLogin()

export default autoLogin
